<template>
  <Section
    :id="id"
    :header="
      t('common.createdParam', {
        param: t('shipments.shipments', createdShipments.length ?? 0),
      })
    "
    :loading="isAnyLoading"
    class="route-builder-executed-shipments-section"
  >
    <template v-if="isAnyErrors" #backButton>
      <RygenButton
        mode="tertiary"
        class="back-button"
        :icon="PrimeIcons.ARROW_LEFT"
        :label="t('common.backToParam', { param: t('common.rating') })"
        @click="emit('back')"
      />
    </template>

    <template #default>
      <RouteBuilderRateShopCard
        :id="`${type}-rates`"
        :type="type"
        :mileage="totalMileage"
        :rate-quotes="createdShipments"
        expanded
      />
    </template>

    <template v-if="searchRoute" #footer>
      <RygenButton
        mode="primary"
        as="router-link"
        :icon="PrimeIcons.MAP"
        :label="
          t('common.viewParam', {
            param: t('shipments.shipments', createdShipments.length),
          })
        "
        :to="searchRoute"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { RateSelectionReason } from '@/model';
import {
  ProposedShipmentCreationError,
  ProposedShipmentCreationLoadable,
  ProposedShipmentRateQuote,
  RateQuoteGroupType,
} from '@/model/routeBuilder';
import { buildSearchRoute } from '@/util';

import RouteBuilderRateShopCard from '@/components/routeBuilder/rateShop/RouteBuilderRateShopCard.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  rateQuotes: {
    type: Array as PropType<ProposedShipmentRateQuote[]>,
    required: true,
  },
  type: {
    type: String as PropType<RateQuoteGroupType>,
    required: true,
  },
  rateSelectionReason: {
    type: Object as PropType<RateSelectionReason>,
    default: () => undefined,
  },
});
const { id, rateQuotes, type, rateSelectionReason } = toRefs(props);
const emit = defineEmits<{
  (event: 'back'): void;
}>();

const { t } = useI18n();
const toast = useToast();

const createdShipments: Ref<ProposedShipmentCreationLoadable[]> = ref([]);

const isAnyLoading = computed(() =>
  createdShipments.value.some((r) => r.loading),
);

const isAnyErrors = computed(() =>
  createdShipments.value.some((r) => !!r.error),
);

const createdShipmentIds = computed(() => {
  return createdShipments.value
    .map((s) => s.data?.result?.shipmentId)
    .filter(notNull);
});

const searchRoute = computed(() => {
  return createdShipmentIds.value.length
    ? buildSearchRoute('shipmentList', { shipmentId: createdShipmentIds.value })
    : undefined;
});

const totalMileage = computed(() => {
  const mileage = createdShipments.value
    .map((r) => r.data?.result?.mileage ?? 0)
    .reduce((a, b) => a + b, 0);

  return Math.round(mileage);
});

const createShipment = (
  { proposedShipment, rateQuote }: ProposedShipmentRateQuote,
  index: number,
) => {
  if (!proposedShipment) {
    throw new Error('proposedShipment is required to create a new shipment');
  }

  if (!createdShipments.value[index]) {
    createdShipments.value[index] = { loading: true };
  }

  const identifier = proposedShipment.identifier;
  createdShipments.value[index].loading = true;
  api.shipmentPlanningConsolidation
    .createProposedShipmentBeta(
      proposedShipment,
      rateQuote,
      rateSelectionReason?.value,
    )
    .then((result) => {
      createdShipments.value[index].data = new ProposedShipmentRateQuote({
        proposedShipment,
        result,
        rateQuote,
      });
    })
    .catch((e) => {
      createdShipments.value[index].error = new ProposedShipmentCreationError({
        proposedShipment,
        rateQuote,
        cause: e,
      });
      console.error(`Error creating proposed shipment ${identifier}`, e);
      toast.addError({
        detail: t('errors.creatingParam', {
          param: `${t('shipments.shipments')} ${identifier}`,
        }),
        error: e,
      });
    })
    .finally(() => {
      createdShipments.value[index].loading = false;
    });
};

const createAllShipments = (shipments: ProposedShipmentRateQuote[]) => {
  shipments.forEach((shipment, index) => createShipment(shipment, index));
};

watch(
  rateQuotes,
  (newVal: ProposedShipmentRateQuote[]) => {
    createAllShipments(newVal);
  },
  { deep: true, immediate: true },
);
</script>

<style>
.route-builder-executed-shipments-section {
  .section-header {
    .section-header-left {
      flex-flow: column;
      align-items: flex-start;
    }
  }

  .section-data {
    display: flex;
    flex-flow: column;
    gap: var(--gap-1);
    height: 100%;
    overflow: auto;
    padding: var(--route-builder-rate-shop-padding);
  }

  .section-footer {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
